import { X, Info } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

interface DiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    plan: string,
    discount: number,
    durationMonths: number | null
  ) => void;
  isPending?: boolean;
}

export default function DiscountModal({
  isOpen,
  onClose,
  onSubmit,
  isPending,
}: DiscountModalProps) {
  const [plan, setPlan] = useState('plus_monthly');
  const [discount, setDiscount] = useState('');
  const [duration, setDuration] = useState('');

  if (!isOpen) return null;

  const isAnnual = plan === 'plus_annual';
  const handlePlanChange = (next: string) => {
    setPlan(next);
    if (next === 'plus_annual') setDuration('');
  };

  const handleSubmit = () => {
    const discountNum = parseInt(discount);
    if (isNaN(discountNum) || discountNum < 1 || discountNum > 100) {
      alert('Please enter a valid discount between 1 and 100');
      return;
    }

    let durationMonths: number | null = null;
    if (!isAnnual && duration.trim() !== '') {
      const durationNum = parseInt(duration);
      if (isNaN(durationNum) || durationNum < 1) {
        alert('Duration must be at least 1 month, or leave empty for forever');
        return;
      }
      durationMonths = durationNum;
    }

    onSubmit(plan, discountNum, durationMonths);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={onClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Generate Discount Code</CardTitle>
              <CardDescription>
                Create a single-use discount code for this user
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Plan Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => handlePlanChange('plus_monthly')}
                className={`flex-1 px-4 py-2 rounded-md border transition-colors ${
                  plan === 'plus_monthly'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background hover:bg-muted'
                }`}
              >
                Plus Monthly
              </button>
              <button
                onClick={() => handlePlanChange('plus_annual')}
                className={`flex-1 px-4 py-2 rounded-md border transition-colors ${
                  plan === 'plus_annual'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background hover:bg-muted'
                }`}
              >
                Plus Annual
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Discount Percentage (1-100)
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="e.g., 50"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Duration (months)
            </label>
            <input
              type="number"
              min="1"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder={
                isAnnual
                  ? 'Applies forever for annual plans'
                  : 'Leave empty to apply forever'
              }
              disabled={isAnnual}
              className="w-full px-3 py-2 border rounded-md bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {isAnnual ? (
              <div className="flex items-start gap-1.5 mt-2 text-xs text-amber-600 dark:text-amber-400">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Plus Annual plans can't use a months duration — Stripe applies
                  repeating month-based coupons inconsistently across yearly
                  billing cycles. The discount will apply forever instead.
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                How many months the discount applies for. Leave empty to apply
                forever.
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={isPending || !discount}
              className="flex-1"
            >
              {isPending ? 'Generating...' : 'Generate Code'}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
